import {
  BenchlingAppType,
  BenchlingAppUiBlockType,
  DatasetUploadStatus,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';

import {GetCanvas} from '../canvases/__generated/GetCanvas';

type GetCanvasUIBlockValueMulti = ExtractByTypename<
  ExtractByTypename<
    GetCanvas['benchlingAppCanvas'],
    'BenchlingAppCanvasNode'
  >['uiBlocks'][number]['blockValue'],
  'UIBlockValueMulti'
>;

type GetCanvasUIBlockValueSingle = ExtractByTypename<
  ExtractByTypename<
    GetCanvas['benchlingAppCanvas'],
    'BenchlingAppCanvasNode'
  >['uiBlocks'][number]['blockValue'],
  'UIBlockValueSingle'
>;

interface UiBlockBase {
  type: BenchlingAppUiBlockType;
  userDefinedId: string;
}

export interface InteractiveUiBlock extends UiBlockBase {
  id: string;
  enabled: boolean;
  canvasEnabled: boolean;
}

export interface UserInputBlock extends InteractiveUiBlock {
  value: string | null;
  label: string | null;
  blockValue?: UIBlockValueSingle | null;
  required: boolean | null;
}

export type UIBlockValueSingle = GetCanvasUIBlockValueSingle;
export type UIBlockValueMulti = GetCanvasUIBlockValueMulti;

export interface UserInputMultiValueBlock extends InteractiveUiBlock {
  value?: Array<string> | null;
  label: string | null;
  blockValue: UIBlockValueMulti | null;
  required: boolean | null;
}

export interface TextInput extends UserInputBlock {
  type: BenchlingAppUiBlockType.TEXT_INPUT;
  placeholder: string | null;
}

export interface FileUpload extends UserInputMultiValueBlock {
  type: BenchlingAppUiBlockType.FILE_UPLOAD;
  fileTypes: Array<string> | null;
  maxFiles: number;
}

export interface Button extends InteractiveUiBlock {
  userDefinedId: string;
  type: BenchlingAppUiBlockType.BUTTON;
  text: string;
}

export interface Markdown extends UiBlockBase {
  type: BenchlingAppUiBlockType.MARKDOWN;
  value: string;
}

export interface Section extends UiBlockBase {
  type: BenchlingAppUiBlockType.SECTION;
  children: Array<UiBlock>;
}

export interface Chip extends UiBlockBase {
  type: BenchlingAppUiBlockType.CHIP;
  value: string;
}

export type SearchInputItemType =
  | BenchlingAppType.dna_sequence
  | BenchlingAppType.dna_oligo
  | BenchlingAppType.aa_sequence
  | BenchlingAppType.custom_entity
  | BenchlingAppType.mixture
  | BenchlingAppType.box
  | BenchlingAppType.container
  | BenchlingAppType.location
  | BenchlingAppType.plate
  | BenchlingAppType.worklist;

interface SearchInputBase {
  itemType: SearchInputItemType;
  schemaId: string | null;
  placeholder: string | null;
}

export interface SearchInput extends UserInputBlock, SearchInputBase {
  type: BenchlingAppUiBlockType.SEARCH_INPUT;
}

export interface SearchInputMultiValue extends UserInputMultiValueBlock, SearchInputBase {
  type: BenchlingAppUiBlockType.SEARCH_INPUT_MULTIVALUE;
}
export interface SelectorInput extends UserInputBlock {
  type: BenchlingAppUiBlockType.SELECTOR_INPUT;
  options: Array<string>;
  placeholder: string | null;
}

export interface SelectorInputMultiValue extends UserInputMultiValueBlock {
  type: BenchlingAppUiBlockType.SELECTOR_INPUT_MULTIVALUE;
  options: Array<string>;
  placeholder: string | null;
}

export interface Dropdown extends UserInputBlock {
  type: BenchlingAppUiBlockType.DROPDOWN;
  dropdownId: string;
  placeholder: string | null;
}

export interface DropdownMultiValue extends UserInputMultiValueBlock {
  type: BenchlingAppUiBlockType.DROPDOWN_MULTIVALUE;
  dropdownId: string;
  placeholder: string | null;
}

export interface Table extends InteractiveUiBlock {
  type: BenchlingAppUiBlockType.TABLE;
  datasetId: string;
  datasetUploadStatus: DatasetUploadStatus;
  name: string;
}

export type UiBlock =
  | Button
  | Chip
  | Dropdown
  | DropdownMultiValue
  | FileUpload
  | Markdown
  | SearchInput
  | SearchInputMultiValue
  | Section
  | SelectorInput
  | SelectorInputMultiValue
  | TextInput
  | Table;

const USER_INPUT_TYPES = [
  BenchlingAppUiBlockType.TEXT_INPUT,
  BenchlingAppUiBlockType.SEARCH_INPUT,
  BenchlingAppUiBlockType.SELECTOR_INPUT,
  BenchlingAppUiBlockType.DROPDOWN,
  BenchlingAppUiBlockType.DROPDOWN_MULTIVALUE,
  BenchlingAppUiBlockType.FILE_UPLOAD,
  BenchlingAppUiBlockType.SELECTOR_INPUT_MULTIVALUE,
  BenchlingAppUiBlockType.SEARCH_INPUT_MULTIVALUE,
];

export function isUserInputBlock(
  block: UiBlock
): block is (UserInputBlock | UserInputMultiValueBlock) & UiBlock {
  return USER_INPUT_TYPES.includes(block.type);
}

export function isMultiValueUserInputBlock(
  block: UserInputBlock | UserInputMultiValueBlock
): block is UserInputMultiValueBlock {
  return block?.blockValue?.__typename === 'UIBlockValueMulti';
}
